import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaChair,
  FaUsers,
  FaBuilding,
  FaDollarSign,
  FaImage,
  FaTimes,
  FaSave,
  FaCancel,
  FaInfoCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import Select from "react-select";
import { useArenaAuth } from "../../context/ArenaAuthContext";

const SportsManagement = () => {
  const [sports, setSports] = useState([]);
  const [sportOptions, setSportOptions] = useState([]);
  const [currentSport, setCurrentSport] = useState({
    id: null,
    name: "",
    facility: "",
    standingCapacity: "",
    sittingCapacity: "",
    rate: "",
    arenaId: null,
    images: [],
    imagePreviews: [],
  });
  const [isEditing, setIsEditing] = useState(false);
  const { arenaUser } = useArenaAuth();

  const fetchSports = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/sports-facility-info/${arenaUser?.arenaId}`,
        { withCredentials: true }
      );
      setSports(response.data);
    } catch (err) {
      console.error("Error fetching sports data:", err);
    }
  };

  const fetchSportOptions = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/common/get-sports`
      );
      const allSportsData = response.data
        .filter((sport) => sport.value !== "General")
        .map((sport) => ({
          value: sport.value,
          label: sport.value,
        }));

      const availableSports = allSportsData.filter(
        (sportOption) =>
          !sports.some((sport) => sport.sportNames === sportOption.value)
      );

      setSportOptions(availableSports);
    } catch (error) {
      console.error("Error fetching sports:", error);
      toast.error("Error loading sports data. Please refresh the page.");
    }
  };

  useEffect(() => {
    if (arenaUser?.arenaId) {
      setCurrentSport((prev) => ({ ...prev, arenaId: arenaUser.arenaId }));
      fetchSports();
      fetchSportOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arenaUser?.arenaId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSport({ ...currentSport, [name]: value === "" ? null : value });
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const currentImages = currentSport.images || [];
    const MAX_SIZE_BYTES = 400 * 1024; // 400KB target
    const MAX_ALLOWED_SIZE = 10 * 1024 * 1024; // 10MB limit

    // Check if adding these files would exceed the 3 image limit
    if (currentImages.length + files.length > 3) {
      toast.error(
        `You can only upload a maximum of 3 images per facility. Currently you have ${currentImages.length} images.`
      );
      e.target.value = ""; // Reset file input
      return;
    }

    // Check for files exceeding 10MB limit
    const oversizedFiles = files.filter((file) => file.size > MAX_ALLOWED_SIZE);
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((file) => file.name).join(", ");
      toast.error(
        `The following files exceed the maximum allowed size of 10MB: ${fileNames}. Please select smaller files.`
      );
      e.target.value = "";
      return;
    }

    // Process each file with compression and convert to base64
    const processFiles = async () => {
      const newImages = [...currentImages];
      const newPreviews = [...(currentSport.imagePreviews || [])];

      for (const file of files) {
        try {
          let processedFile = file;

          if (file.size > MAX_SIZE_BYTES) {
            console.log(
              `Compressing facility image ${file.name} from ${(
                file.size / 1024
              ).toFixed(1)}KB...`
            );

            const originalSizeKB = file.size / 1024;

            if (originalSizeKB <= 1000) {
              // For images 400KB - 1MB: Light compression
              processedFile = await imageCompression(file, {
                maxSizeMB: 0.38, // 380KB target
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                fileType: "image/jpeg",
                initialQuality: 0.85,
              });
            } else if (originalSizeKB <= 3000) {
              // For images 1MB - 3MB: Moderate compression
              processedFile = await imageCompression(file, {
                maxSizeMB: 0.35, // 350KB target
                maxWidthOrHeight: 1600,
                useWebWorker: true,
                fileType: "image/jpeg",
                initialQuality: 0.75,
              });
            } else {
              // For images 3MB+: More aggressive compression
              processedFile = await imageCompression(file, {
                maxSizeMB: 0.32, // 320KB target
                maxWidthOrHeight: 1400,
                useWebWorker: true,
                fileType: "image/jpeg",
                initialQuality: 0.65,
              });
            }

            // Stage 2: If still too large, compress more
            if (processedFile.size > MAX_SIZE_BYTES) {
              console.log(
                `Stage 2: First compression result ${(
                  processedFile.size / 1024
                ).toFixed(1)}KB, compressing more...`
              );
              processedFile = await imageCompression(processedFile, {
                maxSizeMB: 0.35,
                maxWidthOrHeight: 1200,
                useWebWorker: true,
                fileType: "image/jpeg",
                initialQuality: 0.6,
              });
            }

            // Stage 3: Final compression if still needed
            if (processedFile.size > MAX_SIZE_BYTES) {
              console.log(
                `Stage 3: Second compression result ${(
                  processedFile.size / 1024
                ).toFixed(1)}KB, final compression...`
              );
              processedFile = await imageCompression(processedFile, {
                maxSizeMB: 0.38,
                maxWidthOrHeight: 1000,
                useWebWorker: true,
                fileType: "image/jpeg",
                initialQuality: 0.5,
              });
            }

            console.log(
              `Final facility image size: ${(processedFile.size / 1024).toFixed(
                1
              )}KB (from ${(file.size / 1024).toFixed(1)}KB)`
            );

            if (processedFile.size > MAX_SIZE_BYTES) {
              console.warn(
                `Warning: Image ${
                  file.name
                } could not be compressed below 400KB. Final size: ${(
                  processedFile.size / 1024
                ).toFixed(1)}KB`
              );
              toast.warning(
                `Image "${
                  file.name
                }" could not be compressed below 400KB. Final size: ${(
                  processedFile.size / 1024
                ).toFixed(1)}KB. It may affect performance.`
              );
            }
          } else {
            console.log(
              `Facility image ${file.name} is already small enough: ${(
                file.size / 1024
              ).toFixed(1)}KB - using original`
            );
          }

          const base64String = await fileToBase64(processedFile);
          newImages.push(base64String);
          newPreviews.push(base64String);
        } catch (error) {
          console.error("Error processing facility image:", error);
          toast.error(`Error processing file ${file.name}. Please try again.`);
          continue;
        }
      }

      setCurrentSport({
        ...currentSport,
        images: newImages,
        imagePreviews: newPreviews,
      });
    };

    processFiles();
  };

  const removeImage = (index) => {
    const newImages = [...currentSport.images];
    const newPreviews = [...currentSport.imagePreviews];

    newImages.splice(index, 1);
    newPreviews.splice(index, 1);

    setCurrentSport({
      ...currentSport,
      images: newImages,
      imagePreviews: newPreviews,
    });
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!(file instanceof Blob)) {
        console.error("Invalid file object:", file);
        reject(new Error("Invalid file object"));
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const validateForm = () => {
    if (!currentSport.name) {
      toast.error("Please select a sport");
      return false;
    }

    if (!currentSport.facility || currentSport.facility.trim() === "" || currentSport.facility === "null") {
      toast.error("Facility information is required");
      return false;
    }

    if (!currentSport.rate || currentSport.rate === "" || currentSport.rate === 0) {
      toast.error("Rate is required");
      return false;
    }

    const rateValue = parseFloat(currentSport.rate);
    if (isNaN(rateValue) || rateValue <= 0) {
      toast.error("Rate must be a valid positive number");
      return false;
    }

    if (!isEditing && (!currentSport.images || currentSport.images.length === 0)) {
      toast.error("At least one facility image is required");
      return false;
    }

    if (currentSport.images && currentSport.images.length > 3) {
      toast.error("Maximum 3 images are allowed");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      if (isEditing) {
        const response = await axios.put(
          `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/sports-facility-info-update`,
          currentSport,
          { withCredentials: true }
        );
        setSports(
          sports.map((sport) =>
            sport.id === currentSport.id
              ? {
                  ...sport,
                  facility: currentSport.facility,
                  standingCapacity: currentSport.standingCapacity,
                  sittingCapacity: currentSport.sittingCapacity,
                  rate: currentSport.rate,
                  sportNames: currentSport.name,
                }
              : sport
          )
        );
        if (response.status === 200) {
          toast.success(response.data.message || "Sport updated successfully!");
          console.log(response.data);
          await fetchSports();
          fetchSportOptions();
        }
      } else {
        currentSport.arenaId = arenaUser?.arenaId;

        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/sports-facility-info-add`,
          {
            ...currentSport,
            images: currentSport.images || [],
          },
          { withCredentials: true }
        );
        const newId = Math.max(...sports.map((s) => s.id), 0) + 1;
        setSports([
          ...sports,
          { ...currentSport, id: newId, sportNames: currentSport.name },
        ]);
        if (response.status === 200) {
          toast.success(response.data.message || "Sport added successfully!");
          await fetchSports();
          fetchSportOptions();
        } else if (response.status === 400) {
          toast.error(response.data.error || "Failed to add new sport/facility");
          console.log(response.data.error);
        } else {
          toast.error("Failed to add new sport/facility");
          console.log(response.data.error);
        }
      }

      resetForm();
    } catch (err) {
      console.error("Error submitting sports data:", err);
      toast.error("An error occurred while saving the sport. Please try again.");
    }
  };

  const handleEdit = async (sport) => {
    // Fetch existing images for this facility
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/facility-images/${sport.id}`
      );

      setCurrentSport({
        id: sport.id,
        name: sport.sportNames,
        facility: sport.aboutFacility ?? "",
        standingCapacity: sport.spectatorStandingCapacity ?? "",
        sittingCapacity: sport.spectatorSeatingCapacity ?? "",
        rate: sport.rate ?? "",
        images: [],
        imagePreviews: [],
        existingImages: response.data || [],
      });
    } catch (error) {
      console.error("Error fetching facility images:", error);
      setCurrentSport({
        id: sport.id,
        name: sport.sportNames,
        facility: sport.aboutFacility ?? "",
        standingCapacity: sport.spectatorStandingCapacity ?? "",
        sittingCapacity: sport.spectatorSeatingCapacity ?? "",
        rate: sport.rate ?? "",
        images: [],
        imagePreviews: [],
        existingImages: [],
      });
    }
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/sports-facility-info/${id}`
      );

      if (response.status === 200) {
        toast.success(response.data.message || "Sport deleted successfully!");
        await fetchSports();
        fetchSportOptions();
      } else {
        toast.error(response.data.error || "Failed to delete sport");
      }
    } catch (err) {
      console.error("Error deleting sports facility:", err.message || err);
      toast.error("An error occurred while deleting the sport. Please try again.");
    }
  };

  const resetForm = () => {
    setCurrentSport({
      id: null,
      name: "",
      facility: "",
      standingCapacity: "",
      sittingCapacity: "",
      rate: "",
      arenaId: arenaUser?.arenaId || null,
      images: [],
      imagePreviews: [],
      existingImages: [],
    });
    setIsEditing(false);
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
          <FaBuilding className="text-[#0071bc] text-lg sm:text-xl md:text-2xl" />
          <span className="truncate">Sports & Facilities</span>
        </h2>
        <p className="text-gray-600 text-xs sm:text-sm md:text-base">Manage your arena's sports facilities and pricing</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl border border-gray-200 mb-4 sm:mb-6 md:mb-8 shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
          <div className="space-y-1 sm:space-y-2">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1 sm:gap-2">
              <FaBuilding className="text-[#0071bc] text-xs sm:text-sm" />
              {isEditing ? "Sport Name" : "Select Sport"} 
              <span className="text-red-500">*</span>
            </label>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={currentSport.name}
                onChange={handleInputChange}
                placeholder="Sport Name"
                disabled
                required
                className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent bg-gray-100 cursor-not-allowed"
              />
            ) : (
              <Select
                placeholder="Select a sport"
                value={
                  currentSport.name
                    ? { value: currentSport.name, label: currentSport.name }
                    : null
                }
                onChange={(option) =>
                  setCurrentSport({ ...currentSport, name: option.value })
                }
                options={sportOptions}
                className="react-select-container text-sm sm:text-base"
                classNamePrefix="react-select"
                isSearchable
                required
                styles={{
                  control: (provided) => ({
                    ...provided,
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '4px',
                    boxShadow: 'none',
                    minHeight: '38px',
                    '&:hover': {
                      border: '1px solid #0071bc',
                    },
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? '#0071bc' : state.isFocused ? '#f0f8ff' : 'white',
                    color: state.isSelected ? 'white' : '#374151',
                    fontSize: '14px',
                  }),
                }}
              />
            )}
          </div>

          <div className="space-y-1 sm:space-y-2">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700">
              Facility Information <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="facility"
              value={currentSport.facility !== null ? currentSport.facility : ""}
              onChange={handleInputChange}
              placeholder="Enter facility information"
              required
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
            />
          </div>

          <div className="space-y-1 sm:space-y-2">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1 sm:gap-2">
              <FaUsers className="text-green-600 text-xs sm:text-sm" />
              Standing Capacity <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <input
              type="number"
              name="standingCapacity"
              value={currentSport.standingCapacity ?? ""}
              onChange={handleInputChange}
              placeholder="Enter standing capacity"
              min="0"
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
            />
          </div>

          <div className="space-y-1 sm:space-y-2">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1 sm:gap-2">
              <FaChair className="text-blue-600 text-xs sm:text-sm" />
              Sitting Capacity <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <input
              type="number"
              name="sittingCapacity"
              value={currentSport.sittingCapacity ?? ""}
              onChange={handleInputChange}
              placeholder="Enter sitting capacity"
              min="0"
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
            />
          </div>

          <div className="space-y-1 sm:space-y-2 md:col-span-2">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1 sm:gap-2">
              <FaDollarSign className="text-green-600 text-xs sm:text-sm" />
              Rate <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="rate"
              value={currentSport.rate ?? ""}
              onChange={handleInputChange}
              placeholder="Enter rate"
              min="0"
              step="0.01"
              required
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
            />
            <p className="text-xs sm:text-sm text-gray-600">Rate for 1 hour slot</p>
          </div>
        </div>

        {/* Image Upload Section */}
        {!isEditing && (
          <div className="space-y-2 sm:space-y-4 mb-4 sm:mb-6">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1 sm:gap-2">
              <FaImage className="text-purple-600 text-xs sm:text-sm" />
              Upload Facility Images <span className="text-red-500">*</span>
            </label>
            <p className="text-xs sm:text-sm text-gray-600">Max 3 images, 10MB each - automatically compressed</p>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageChange}
              required
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm border-2 border-dashed border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 hover:border-[#0071bc]"
            />
            <p className="text-xs sm:text-sm text-gray-500">
              {currentSport.images.length}/3 images uploaded
            </p>

            {/* Image Previews */}
            {currentSport.imagePreviews.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 mt-2 sm:mt-4">
                {currentSport.imagePreviews.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-20 sm:h-28 md:h-32 object-cover rounded-lg sm:rounded-xl border-2 border-gray-200 shadow-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-red-500 text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs sm:text-sm hover:bg-red-600 transition-colors shadow-lg"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Show existing images during edit (read-only) */}
        {isEditing &&
          currentSport.existingImages &&
          currentSport.existingImages.length > 0 && (
            <div className="space-y-2 sm:space-y-4 mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-semibold text-gray-700">Existing Facility Images (Read-only)</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                {currentSport.existingImages.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={`data:image/jpeg;base64,${image}`}
                      alt={`Existing ${index + 1}`}
                      className="w-full h-20 sm:h-28 md:h-32 object-cover rounded-lg sm:rounded-xl border-2 border-gray-200 shadow-lg"
                    />
                    <div className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-blue-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-md sm:rounded-lg text-[10px] sm:text-xs font-semibold">
                      Existing
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs sm:text-sm text-gray-600">
                Note: Images cannot be edited. Contact admin to change facility images.
              </p>
            </div>
          )}

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-end">
          <button 
            type="submit" 
            className="bg-[#0071bc] text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:bg-[#005a94] transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
          >
            {isEditing ? <FaEdit className="text-sm" /> : <FaPlus className="text-sm" />}
            {isEditing ? "Update Sport" : "Add Sport"}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={resetForm}
              className="bg-gray-500 text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:bg-gray-600 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
            >
              <FaTimes className="text-sm" />
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">Added Sports & Facilities</h3>
          <div className="text-xs sm:text-sm text-gray-600 bg-blue-50 px-2 sm:px-3 py-1 rounded-full self-start sm:self-auto">
            {sports.length} {sports.length === 1 ? 'Sport' : 'Sports'} Added
          </div>
        </div>

        {sports.map((sport) => (
          <div key={sport.id} className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex flex-col gap-3 sm:gap-4 md:gap-6">
              <div className="flex-1">
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4 flex items-center gap-2">
                  <FaBuilding className="text-[#0071bc] text-sm sm:text-base md:text-lg flex-shrink-0" />
                  <span className="truncate">{sport.sportNames}</span>
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                  <div className="flex items-start gap-1 sm:gap-2 text-gray-700">
                    <FaInfoCircle className="text-blue-500 text-xs sm:text-sm mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <span className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-500 block">Facility</span>
                      <p className="font-semibold text-xs sm:text-sm md:text-base truncate">{sport.aboutFacility}</p>
                    </div>
                  </div>
                  {sport.spectatorStandingCapacity && (
                    <div className="flex items-start gap-1 sm:gap-2 text-gray-700">
                      <FaUsers className="text-green-500 text-xs sm:text-sm mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-500 block">Standing</span>
                        <p className="font-semibold text-xs sm:text-sm md:text-base">{sport.spectatorStandingCapacity}</p>
                      </div>
                    </div>
                  )}
                  {sport.spectatorSeatingCapacity && (
                    <div className="flex items-start gap-1 sm:gap-2 text-gray-700">
                      <FaChair className="text-blue-500 text-xs sm:text-sm mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-500 block">Sitting</span>
                        <p className="font-semibold text-xs sm:text-sm md:text-base">{sport.spectatorSeatingCapacity}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-1 sm:gap-2 text-gray-700">
                    <FaDollarSign className="text-green-500 text-xs sm:text-sm mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-500 block">Rate</span>
                      <p className="font-semibold text-xs sm:text-sm md:text-base">${sport.rate ?? "N/A"}/hr</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 sm:gap-3 justify-end">
                <button
                  onClick={() => handleEdit(sport)}
                  className="bg-blue-600 text-white px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium hover:bg-blue-700 transition-all duration-300 flex items-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl"
                >
                  <FaEdit className="text-xs sm:text-sm" />
                  <span className="hidden xs:inline">Edit</span>
                </button>
                <button
                  onClick={() => handleDelete(sport.id)}
                  className="bg-red-600 text-white px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium hover:bg-red-700 transition-all duration-300 flex items-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl"
                >
                  <FaTrash className="text-xs sm:text-sm" />
                  <span className="hidden xs:inline">Delete</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {sports.length === 0 && (
        <div className="text-center py-8 sm:py-12 md:py-16 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl sm:rounded-2xl border-2 border-dashed border-gray-300">
          <FaExclamationTriangle className="text-4xl sm:text-5xl md:text-6xl text-gray-400 mx-auto mb-2 sm:mb-4" />
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700 mb-1 sm:mb-2">No Sports Added Yet</h3>
          <p className="text-gray-600 text-xs sm:text-sm md:text-base px-4">Add your first sport facility using the form above to get started.</p>
        </div>
      )}
    </div>
  );
};

export default SportsManagement;
